import { useEffect, useState } from 'react';
import { supabase } from '@/services/supabase';
import type { Role } from '@asi/shared';
import type { Permission } from '@asi/shared';
import {
  ShieldCheck,
  Plus,
  Pencil,
  Trash2,
  X,
  Lock,
  Check,
  Search,
  ChevronDown,
  ChevronRight,
  Loader2,
  Users,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/components/PermissionGate';
import { usePermissions } from '@/hooks/usePermissions';
import { AccountsTabs } from '@/components/AccountsTabs';

// ─── Permission Groups ────────────────────────────────────────

interface PermGroup {
  label: string;
  permissions: { key: Permission; label: string }[];
}

const PERMISSION_GROUPS: PermGroup[] = [
  {
    label: 'Panel y Emergencias',
    permissions: [
      { key: 'dashboard.view', label: 'Ver dashboard' },
      { key: 'emergency.view', label: 'Ver centro de emergencias' },
      { key: 'emergency.assign_patrol', label: 'Asignar patrullero' },
      { key: 'emergency.update_status', label: 'Actualizar estado de alerta' },
    ],
  },
  {
    label: 'Historial de Tickets',
    permissions: [
      { key: 'tickets.view', label: 'Ver tickets' },
      { key: 'tickets.create', label: 'Crear tickets' },
      { key: 'tickets.update', label: 'Editar tickets' },
      { key: 'tickets.delete', label: 'Eliminar tickets' },
    ],
  },
  {
    label: 'Usuarios',
    permissions: [
      { key: 'users.view', label: 'Ver usuarios' },
      { key: 'users.create', label: 'Crear usuarios' },
      { key: 'users.update', label: 'Editar usuarios' },
      { key: 'users.delete', label: 'Eliminar usuarios' },
      { key: 'users.manage_roles', label: 'Gestionar roles' },
    ],
  },
  {
    label: 'Cámaras',
    permissions: [
      { key: 'cameras.view', label: 'Ver cámaras' },
      { key: 'cameras.manage', label: 'Administrar cámaras' },
    ],
  },
  {
    label: 'Páneles',
    permissions: [
      { key: 'alarms.view', label: 'Ver paneles de alarma' },
      { key: 'alarms.manage', label: 'Administrar paneles' },
    ],
  },
  {
    label: 'Centro ARC',
    permissions: [
      { key: 'arc.events.view', label: 'Ver eventos ARC' },
      { key: 'arc.contacts.view', label: 'Ver contactos ARC' },
      { key: 'arc.contacts.manage', label: 'Gestionar contactos ARC' },
      { key: 'arc.cid_codes.view', label: 'Ver códigos CID' },
      { key: 'arc.cid_codes.manage', label: 'Gestionar códigos CID' },
    ],
  },
  {
    label: 'Cuentas',
    permissions: [
      { key: 'accounts.view', label: 'Ver cuentas' },
      { key: 'accounts.manage', label: 'Administrar cuentas' },
    ],
  },
  {
    label: 'Planes y Facturación',
    permissions: [
      { key: 'plans.view', label: 'Ver planes' },
      { key: 'plans.manage', label: 'Administrar planes y precios' },
      { key: 'revenue.view', label: 'Ver ingresos y suscripciones' },
      { key: 'billing.view', label: 'Ver contratos, suscripciones y productos' },
      { key: 'billing.manage', label: 'Facturación masiva, config FEL y productos' },
      { key: 'collections.view', label: 'Ver cobros y facturas' },
      { key: 'collections.confirm', label: 'Confirmar pagos recibidos' },
    ],
  },
  {
    label: 'Comunicaciones',
    permissions: [
      { key: 'communications.view', label: 'Ver comunicaciones enviadas' },
      { key: 'communications.send', label: 'Enviar push, popups y banners' },
    ],
  },
  {
    label: 'Geocercas y Sistema',
    permissions: [
      { key: 'geofences.view', label: 'Ver geocercas' },
      { key: 'geofences.manage', label: 'Administrar geocercas' },
      { key: 'changelog.view', label: 'Ver historial de cambios' },
      { key: 'settings.manage', label: 'Configuración del sistema' },
      { key: 'audit.view', label: 'Ver registro de auditoría' },
    ],
  },
];

const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.key));

const EMPTY_FORM = {
  name: '',
  label: '',
  description: '',
  color: '#6366f1',
  permissions: [] as Permission[],
};

// ─── Component ────────────────────────────────────────────────

let _cachedRoles: Role[] | null = null;

export function RolesPage() {
  const [roles, setRoles] = useState<Role[]>(_cachedRoles ?? []);
  const [loading, setLoading] = useState(_cachedRoles === null);
  // Keep the id, not the object: after editing a role the list is refetched and the detail panel
  // must show the fresh permissions. Defaults to the first role so the panel is never empty.
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [userCounts, setUserCounts] = useState<Record<string, number>>({});
  const [onlyGranted, setOnlyGranted] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Read-only detail panel: search + collapse state for the permission matrix.
  const [viewPermSearch, setViewPermSearch] = useState('');
  const [viewCollapsedGroups, setViewCollapsedGroups] = useState<Set<string>>(new Set());
  // Edit/create modal: search + collapse state for the permission matrix.
  const [permSearch, setPermSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const { can } = usePermissions();

  function matchesQuery(group: PermGroup, q: string) {
    if (!q) return group.permissions;
    return group.permissions.filter(
      (p) =>
        p.label.toLowerCase().includes(q) ||
        p.key.toLowerCase().includes(q) ||
        group.label.toLowerCase().includes(q),
    );
  }

  function toggleCollapsed(setter: typeof setCollapsedGroups, label: string) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  async function fetchRoles() {
    if (_cachedRoles === null) setLoading(true);
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .order('created_at', { ascending: true });
    if (!error && data) {
      _cachedRoles = data as Role[];
      setRoles(_cachedRoles);
    }
    setLoading(false);
  }

  useEffect(() => { fetchRoles(); }, []);

  // Active (not deleted) users per role, shown next to each role.
  useEffect(() => {
    supabase
      .from('profiles')
      .select('role')
      .is('deleted_at', null)
      .then(({ data }) => {
        const counts: Record<string, number> = {};
        for (const { role } of (data ?? []) as { role: string }[]) counts[role] = (counts[role] ?? 0) + 1;
        setUserCounts(counts);
      });
  }, []);

  const selectedRole = roles.find((r) => r.id === selectedRoleId) ?? roles[0] ?? null;

  function openCreate() {
    setEditingRole(null);
    setForm(EMPTY_FORM);
    setError(null);
    setPermSearch('');
    setCollapsedGroups(new Set());
    setShowModal(true);
  }

  function openEdit(role: Role) {
    setEditingRole(role);
    setForm({
      name: role.name,
      label: role.label,
      description: role.description ?? '',
      color: role.color,
      permissions: role.permissions as Permission[],
    });
    setError(null);
    setPermSearch('');
    setCollapsedGroups(new Set());
    setShowModal(true);
  }

  function togglePermission(perm: Permission) {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter((p) => p !== perm)
        : [...f.permissions, perm],
    }));
  }

  function toggleGroup(group: PermGroup) {
    const keys = group.permissions.map((p) => p.key);
    const allActive = keys.every((k) => form.permissions.includes(k));
    setForm((f) => ({
      ...f,
      permissions: allActive
        ? f.permissions.filter((p) => !keys.includes(p))
        : [...new Set([...f.permissions, ...keys])],
    }));
  }

  async function handleSave() {
    if (!form.label.trim()) { setError('El nombre del rol es obligatorio.'); return; }
    if (!editingRole && !form.name.trim()) { setError('El identificador (slug) es obligatorio.'); return; }
    setSaving(true);
    setError(null);

    if (editingRole) {
      const { error } = await supabase
        .from('roles')
        .update({
          label: form.label.trim(),
          description: form.description.trim() || null,
          color: form.color,
          permissions: form.permissions,
        })
        .eq('id', editingRole.id);
      if (error) { setError(error.message); setSaving(false); return; }
    } else {
      const slug = form.name.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const { error } = await supabase.from('roles').insert({
        name: slug,
        label: form.label.trim(),
        description: form.description.trim() || null,
        color: form.color,
        permissions: form.permissions,
        is_system: false,
      });
      if (error) { setError(error.message); setSaving(false); return; }
    }

    setSaving(false);
    setShowModal(false);
    fetchRoles();
  }

  async function handleDelete(role: Role) {
    if (!window.confirm(`¿Eliminar el rol "${role.label}"? Los usuarios con este rol perderán sus permisos.`)) return;
    await supabase.from('roles').delete().eq('id', role.id);
    setRoles((prev) => prev.filter((r) => r.id !== role.id));
    if (selectedRole?.id === role.id) setSelectedRoleId(null);
  }

  const canEdit = can('users.manage_roles');
  const totalPerms = ALL_PERMISSIONS.length;

  return (
    <div className="flex flex-col h-full">
      <AccountsTabs />
      <div className="flex flex-1 min-h-0">
      {/* Left: Roles list */}
      <div className="w-80 shrink-0 border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" /> Roles
            </h2>
            <PermissionGate require="users.manage_roles">
              <Button size="sm" onClick={openCreate} title="Nuevo rol personalizado">
                <Plus className="w-4 h-4 mr-1" /> Nuevo
              </Button>
            </PermissionGate>
          </div>
          <p className="text-xs text-muted-foreground">
            {roles.length} roles · {Object.values(userCounts).reduce((a, b) => a + b, 0)} usuarios
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {roles.map((role) => {
              const users = userCounts[role.name] ?? 0;
              const coverage = Math.round((role.permissions.length / totalPerms) * 100);
              const active = selectedRole?.id === role.id;
              return (
                <button
                  key={role.id}
                  onClick={() => setSelectedRoleId(role.id)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-lg border transition-colors',
                    active ? 'bg-primary/5 border-primary/30' : 'border-transparent hover:bg-muted',
                  )}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: role.color }} />
                    <span className="text-sm font-medium text-foreground truncate flex-1">{role.label}</span>
                    {role.is_system && (
                      <Lock className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" aria-label="Rol de sistema" />
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground" style={{ paddingLeft: '1.375rem' }}>
                    <span className="flex items-center gap-1 shrink-0">
                      <Users className="w-3 h-3" /> {users}
                    </span>
                    <span className="shrink-0">· {role.permissions.length}/{totalPerms}</span>
                    <span className="h-1 flex-1 rounded-full bg-muted overflow-hidden">
                      <span className="block h-full rounded-full" style={{ width: `${coverage}%`, backgroundColor: role.color }} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Right: Permission detail */}
      <div className="flex-1 overflow-auto">
        {!selectedRole ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <ShieldCheck className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">{loading ? 'Cargando roles…' : 'No hay roles definidos'}</p>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Role header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: selectedRole.color + '20', border: `2px solid ${selectedRole.color}40` }}
                >
                  <ShieldCheck className="w-5 h-5" style={{ color: selectedRole.color }} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-bold text-foreground">{selectedRole.label}</h3>
                    {selectedRole.is_system && (
                      <Badge variant="secondary" className="text-xs gap-1"><Lock className="w-2.5 h-2.5" /> Sistema</Badge>
                    )}
                    <code className="text-xs text-muted-foreground">{selectedRole.name}</code>
                  </div>
                  {selectedRole.description && (
                    <p className="text-sm text-muted-foreground mt-0.5">{selectedRole.description}</p>
                  )}
                </div>
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(selectedRole)}>
                    <Pencil className="w-4 h-4 mr-1" /> Editar permisos
                  </Button>
                  {!selectedRole.is_system && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(selectedRole)}
                      className="text-red-600 border-red-500/30 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 gap-3 sm:max-w-md">
              <div className="rounded-lg border border-border p-3">
                <p className="text-2xl font-bold text-foreground">{userCounts[selectedRole.name] ?? 0}</p>
                <p className="text-xs text-muted-foreground">usuarios con este rol</p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-2xl font-bold text-foreground">
                  {selectedRole.permissions.length}
                  <span className="text-sm font-normal text-muted-foreground">/{totalPerms}</span>
                </p>
                <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.round((selectedRole.permissions.length / totalPerms) * 100)}%`, backgroundColor: selectedRole.color }}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">permisos otorgados</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={viewPermSearch}
                  onChange={(e) => setViewPermSearch(e.target.value)}
                  placeholder="Buscar permiso por nombre, clave o módulo..."
                  className="pl-9"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyGranted}
                  onChange={(e) => setOnlyGranted(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                Solo otorgados
              </label>
            </div>

            {/* Permission groups — 2 columns on wide screens */}
            <div className="grid gap-3 xl:grid-cols-2 items-start">
              {PERMISSION_GROUPS.map((group) => {
                const q = viewPermSearch.trim().toLowerCase();
                const visiblePerms = matchesQuery(group, q).filter(
                  (p) => !onlyGranted || selectedRole.permissions.includes(p.key),
                );
                if ((q || onlyGranted) && visiblePerms.length === 0) return null;
                const grantedCount = group.permissions.filter((p) =>
                  selectedRole.permissions.includes(p.key),
                ).length;
                const isCollapsed = !q && viewCollapsedGroups.has(group.label);
                return (
                  <div key={group.label} className="rounded-lg border border-border overflow-hidden">
                    <button
                      onClick={() => toggleCollapsed(setViewCollapsedGroups, group.label)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                        {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        {group.label}
                      </span>
                      <span className={cn(
                        'text-xs px-2 py-0.5 rounded-full',
                        grantedCount === group.permissions.length
                          ? 'bg-green-500/10 text-green-600'
                          : grantedCount === 0 ? 'text-muted-foreground' : 'bg-amber-500/10 text-amber-600',
                      )}>
                        {grantedCount}/{group.permissions.length}
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div className="divide-y divide-border">
                        {visiblePerms.map(({ key, label }) => {
                          const hasIt = selectedRole.permissions.includes(key);
                          return (
                            <div key={key} className="flex items-center justify-between gap-3 px-4 py-2">
                              <div className="min-w-0">
                                <p className={cn('text-sm', hasIt ? 'text-foreground' : 'text-muted-foreground')}>{label}</p>
                                <p className="text-[11px] font-mono text-muted-foreground/60 truncate">{key}</p>
                              </div>
                              {hasIt ? (
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500/15">
                                  <Check className="w-3.5 h-3.5 text-green-600" />
                                </span>
                              ) : (
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted">
                                  <X className="w-3 h-3 text-muted-foreground/60" />
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Edit/Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-slate-800">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingRole ? `Editar rol: ${editingRole.label}` : 'Nuevo rol personalizado'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="overflow-y-auto flex-1 p-5 space-y-5">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                {!editingRole && (
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Identificador <span className="text-gray-400 text-xs">(slug, ej: supervisor_noche)</span>
                    </label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="supervisor_noche"
                      className="font-mono"
                    />
                  </div>
                )}
                <div className={editingRole ? 'col-span-2 sm:col-span-1' : 'col-span-2 sm:col-span-1'}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Nombre visible</label>
                  <Input
                    value={form.label}
                    onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                    placeholder="Supervisor Turno Noche"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-200 dark:border-slate-700 p-0.5"
                    />
                    <Input
                      value={form.color}
                      onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                      className="font-mono flex-1"
                      placeholder="#6366f1"
                    />
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Descripción</label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Descripción del rol y sus responsabilidades..."
                  />
                </div>
              </div>

              {/* Active permissions summary */}
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-gray-700 dark:text-slate-200">Permisos</span>
                <div className="flex items-center gap-3 text-gray-400 dark:text-slate-500">
                  <span>{form.permissions.length} de {ALL_PERMISSIONS.length} seleccionados</span>
                  <button
                    onClick={() => setForm((f) => ({ ...f, permissions: [...ALL_PERMISSIONS] }))}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Todo
                  </button>
                  <button
                    onClick={() => setForm((f) => ({ ...f, permissions: [] }))}
                    className="text-xs text-gray-400 hover:underline"
                  >
                    Ninguno
                  </button>
                </div>
              </div>

              {/* Permission search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.target.value)}
                  placeholder="Buscar permiso por nombre, clave o módulo..."
                  className="pl-9"
                />
              </div>

              {/* Permission checkbox matrix */}
              <div className="space-y-3">
                {PERMISSION_GROUPS.map((group) => {
                  const q = permSearch.trim().toLowerCase();
                  const visiblePerms = matchesQuery(group, q);
                  if (q && visiblePerms.length === 0) return null;
                  const keys = group.permissions.map((p) => p.key);
                  const allChecked = keys.every((k) => form.permissions.includes(k));
                  const someChecked = !allChecked && keys.some((k) => form.permissions.includes(k));
                  const isCollapsed = !q && collapsedGroups.has(group.label);
                  return (
                    <div key={group.label} className="rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
                      {/* Group header: chevron collapses, checkbox toggles all */}
                      <div className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-800/60 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                        <button
                          onClick={() => toggleCollapsed(setCollapsedGroups, group.label)}
                          className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-slate-200 flex-1 text-left"
                        >
                          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          {group.label}
                          <span className="text-xs font-normal text-gray-400 dark:text-slate-500">
                            ({keys.filter((k) => form.permissions.includes(k)).length}/{keys.length})
                          </span>
                        </button>
                        <button
                          onClick={() => toggleGroup(group)}
                          title="Seleccionar/deseleccionar todo el grupo"
                          className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
                            allChecked ? 'bg-blue-500 border-blue-500' :
                            someChecked ? 'bg-blue-200 dark:bg-blue-800 border-blue-300 dark:border-blue-600' :
                            'border-gray-300 dark:border-slate-600'
                          }`}
                        >
                          {(allChecked || someChecked) && <Check className="w-2.5 h-2.5 text-white" />}
                        </button>
                      </div>
                      {!isCollapsed && (
                      <div className="divide-y divide-gray-100 dark:divide-slate-800">
                        {visiblePerms.map(({ key, label }) => {
                          const checked = form.permissions.includes(key);
                          return (
                            <label key={key} className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/40 transition-colors">
                              <div>
                                <span className="text-sm text-gray-800 dark:text-slate-200">{label}</span>
                                <span className="ml-2 text-xs font-mono text-gray-300 dark:text-slate-600">{key}</span>
                              </div>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePermission(key)}
                                className="w-4 h-4 accent-blue-600 cursor-pointer"
                              />
                            </label>
                          );
                        })}
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {error && (
                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex gap-3 p-5 border-t border-gray-200 dark:border-slate-800">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Guardando...' : editingRole ? 'Guardar cambios' : 'Crear rol'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
